import { MouseEvent, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as SettingsOutlinedIcon } from '@elementary/common-ui/assets/settings.svg';
import {
  Checkbox,
  Flex,
  FlexColumn,
  IconButton,
  SvgIcon,
} from '@elementary/common-ui/components';
import {
  INCIDENT_LIST_GROUP_SUMMARY,
  INCIDENT_LIST_PAGED,
  INCIDENTS_SETTINGS,
  TREE_INCIDENT_STATUS_TOTALS,
} from '@elementary/common-ui/constants/queryKeys';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { Divider, Menu as MuiMenu, Tooltip, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from '../api';

const MenuContent = styled(FlexColumn)`
  width: 360px;
`;

const Header = styled(FlexColumn)`
  gap: 4px;
  padding: 16px;
`;

const RuleRow = styled(Flex)`
  gap: 12px;
  padding: 16px;
`;

const IncidentSettingsMenu = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selectedEnvId = useSelectedEnv();
  const hasPermission = useEnvPermission();
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const canEdit = hasPermission('EDIT_INCIDENTS');
  const queryKey = [INCIDENTS_SETTINGS, selectedEnvId];

  const { data: settings, isLoading } = useQuery(queryKey, () =>
    api.fetchIncidentsSettings(selectedEnvId!),
  );

  const invalidateIncidentQueries = () => {
    queryClient.invalidateQueries(queryKey);
    queryClient.invalidateQueries([INCIDENT_LIST_PAGED], { exact: false });
    queryClient.invalidateQueries([INCIDENT_LIST_GROUP_SUMMARY], {
      exact: false,
    });
    queryClient.invalidateQueries([TREE_INCIDENT_STATUS_TOTALS], {
      exact: false,
    });
  };

  const { mutateAsync: updateSettings, isLoading: isUpdating } = useMutation(
    (nextSettings: NonNullable<typeof settings>) =>
      api.updateIncidentsSettings(selectedEnvId!, nextSettings),
    {
      onSuccess: invalidateIncidentQueries,
    },
  );

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFreshnessGroupingChange = async (enabled: boolean) => {
    const nextSettings = {
      enable_freshness_grouping: enabled,
    };

    try {
      await updateSettings(nextSettings);
      openSnackbar({
        variant: 'success',
        message: 'Incident settings updated',
      });
    } catch {
      openSnackbar({
        variant: 'error',
        message: 'Incident settings update failed',
      });
    }
  };

  const disabledTooltip = !canEdit
    ? 'No permissions to modify incident settings'
    : '';

  return (
    <>
      <Tooltip title="Incident settings">
        <IconButton onClick={handleOpen}>
          <SvgIcon width={20}>
            <SettingsOutlinedIcon />
          </SvgIcon>
        </IconButton>
      </Tooltip>

      <MuiMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ sx: { p: 0 } }}
      >
        <MenuContent>
          <Header>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              Auto-merge rules
            </Typography>
            <Typography sx={{ color: 'common.gray4', fontSize: 12 }}>
              Automatically group new incidents with existing open ones when
              they match.
            </Typography>
          </Header>

          <Divider />

          <RuleRow>
            <Tooltip title={disabledTooltip}>
              <Flex sx={{ pt: 0.25 }}>
                <Checkbox
                  $noPadding
                  checked={settings?.enable_freshness_grouping ?? true}
                  onChange={(_, checked) =>
                    handleFreshnessGroupingChange(checked)
                  }
                  disabled={!canEdit || isLoading || isUpdating}
                />
              </Flex>
            </Tooltip>

            <FlexColumn sx={{ gap: 0.25 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                Freshness issues
              </Typography>
              <Typography sx={{ color: 'common.gray4', fontSize: 12 }}>
                Auto-merge freshness incidents that affect the same source as an
                existing open incident.
              </Typography>
            </FlexColumn>
          </RuleRow>
        </MenuContent>
      </MuiMenu>
    </>
  );
};

export default IncidentSettingsMenu;
